import metaInfo from '@/modules/mixins/metaInfo'
import store from '@/modules/store'
import { EventBus } from '@/modules/plugins/modules/event'
import { mapGetters } from 'vuex'
import Spinner from '@/components/atoms/Spinner'
import NotificationAlert from '@/components/organism/restaurants/NotificationAlert.vue'
import Banners from '@/components/organism/restaurants/Banners.vue'
import BannerLanding from '@/components/organism/restaurants/BannerLanding.vue'

// The brand page renders every profile of one brand on a single page and groups them
// by store on the client (spec B7). The brand params (brand / limit / offset) must win
// on EVERY fetch – the initial load and each refresh path (address / delivery time /
// language / logout) go through setList(), which merges them last. Page is the
// exception: an explicit caller page (List.vue's getPage, used by the over-limit
// fallback) wins over the brand default of page 1, so that fallback can page past the
// single BRAND_LIST_LIMIT fetch instead of refetching page 1 forever.
// `offset` is derived from the page, not hardcoded to 0: the core-api search endpoint
// paginates by `offset` whenever it is present at all (even 0), silently ignoring
// `page` - sending a fixed `offset: 0` alongside `page: 2` made the fallback refetch
// page 1's rows a second time under the same "page 2" request (verified against
// /frontend-api/public/restaurant/search directly, 2026-09-15).
export const BRAND_LIST_LIMIT = 200

export function brandListParams(slug, page = 1) {
    const normalizedPage = page || 1
    return {
        brand: slug,
        limit: BRAND_LIST_LIMIT,
        page: normalizedPage,
        offset: (normalizedPage - 1) * BRAND_LIST_LIMIT,
    }
}

export default {
    mixins: [metaInfo],

    components: {
        RestaurantsLoader: () =>
            import(/* webpackPrefetch: true */ '@/components/molecules/RestaurantsLoader'),
        TrackingAlert: () =>
            import(/* webpackPrefetch: true */ '@/components/organism/restaurants/TrackingAlert'),
        CanteenBanner: () => import('@/components/organism/canteen/CanteenBanner'),
        List: () => import(/* webpackPreload: true */ '@/components/organism/restaurants/List'),
        FeaturedRestaurants: () => import('@/components/organism/restaurants/FeaturedRestaurants'),
        PopularRestaurants: () => import('@/components/organism/restaurants/PopularRestaurants'),
        TagSelect: () => import(/* webpackPreload: true */ '@/components/organism/TagSelect'),
        TagsSelect: () => import(/* webpackPreload: true */ '@/components/organism/TagsSelect'),
        Spinner,
        NotificationAlert,
        BannerLanding,
        Banners,
    },

    data() {
        return {
            searchVal: this.$route.query.search || undefined,
            tagsForChangeSlugs: null,
            pageLoading: true,
        }
    },

    computed: {
        ...mapGetters({
            smDevice: 'smDevice',
            showCanteenBanner: 'canteen/showBanner',
            isApple: 'isApple',
            tagsAll: 'tags/all',
            listFiltered: 'site/listFiltered',
            showTagSelect: 'tags/showTagSelect',
            orderAsap: 'order/asap',
            orderTime: 'order/time',
        }),
        pageTitle() {
            return this.$t('labels.restaurants')
        },
        showPopularRestaurants() {
            return !this.listFiltered && this.restaurants.loaded && this.restaurants.popular?.length
        },
        showFeaturedRestaurants() {
            return (
                !this.listFiltered && this.restaurants.loaded && this.restaurants.featured?.length
            )
        },
    },

    watch: {
        $route: {
            deep: true,
            handler(val, oldVal) {
                this.$nextTick(() => {
                    if (val.fullPath !== oldVal.fullPath) {
                        const params = {
                            page: 1,
                        }

                        this.setList(
                            this.restaurants,
                            this.$route,
                            params,
                            { pageLoader: false, resetList: true },
                            false
                        )
                    }
                })
            },
        },
        orderAsap() {
            this.setList(
                this.restaurants,
                this.$route,
                { page: 1 },
                { pageLoader: false, resetList: true },
                false
            )
        },
        orderTime() {
            this.setList(
                this.restaurants,
                this.$route,
                { page: 1 },
                { pageLoader: false, resetList: true },
                false
            )
        },
    },

    mounted() {
        EventBus.$on('language', async () => {
            if (this.$route.query.tag_name || this.$route.query.tag_names) {
                this.tagsForChangeSlugs = [...this.tagsAll]
                await this.$store.dispatch('tags/fetch', this.$route)

                this.setQuery(
                    this.tagsForChangeSlugs.reduce(
                        (p, c) => {
                            if (c.slug === this.$route.query.tag_name) {
                                p.tag_name = this.tagsAll.find((tag) => tag.id === c.id).slug
                            } else if (this.$route.query.tag_names.includes(c.slug)) {
                                p.tag_names.push(this.tagsAll.find((tag) => tag.id === c.id).slug)
                            }
                            return p
                        },
                        { tag_name: undefined, tag_names: [] }
                    )
                )
            } else this.setList(this.restaurants, this.$route)
        })
        this.$store.dispatch('getDeliveryPeriod', {
            restaurantId: undefined,
            allowanceId: this.$store.getters['allowance']?.id,
        })
        this.$user.on('logout', this.onLogout)
    },

    beforeDestroy() {
        EventBus.$off('language')
        this.$user.off('logout', this.onLogout)
    },

    methods: {
        onLogout() {
            this.setList(this.restaurants, this.$route)
        },
        async setList(restaurants, route, params = {}, extraOptions = {}, getTags = true) {
            const brandParams =
                route.name === 'brand-list' ? brandListParams(route.params.slug, params.page) : {}

            return await Promise.all([
                getTags ? store.dispatch('tags/fetch', route) : undefined,
                restaurants.fetchRestaurants(
                    {
                        featured_count: 10,
                        popular_count: 10,
                        ...route.query,
                        limit: undefined, // prevent user meddling
                        items_per_page: undefined, // prevent user meddling
                        ...params,
                        ...brandParams,
                    },
                    extraOptions
                ),
            ])
        },
        next() {
            this.restaurants.next()
        },
    },
}
